// src/pages/time/MsCalendarPage.jsx
// Extracted from App.js - MS Calendar integration view
// BRIO Migration: Event cards code="4c" (list), Recent events code="1b" (table compact)
//
// ADMIN-ONLY ARCHITECTURE:
// - Admin connects once → owns a master "Projects Calendar"
// - All Graph calls go through Edge Functions using admin refresh token
// - Normal users see read-only status + subscription instructions
import React from 'react';
import { Calendar, Clock, RefreshCw, Link, Unlink, Upload, BarChart3, XCircle, Palette } from 'lucide-react';
import {
  BrioAnalyticsSurface,
  BrioFrame,
  BrioByCode,
  BrioTable,
  getTablePropsFromCode
} from '../../components/BrioComponents';

// Presentation codes for this page
const RECENT_EVENTS_CODE = '1b';

export default function MsCalendarPage({
  // Data
  currentUser,
  msCalendarConnected,
  msCalendarEvents,
  msCalendarName,          // NEW: Display name of the master calendar (optional)
  msLastSync,
  msAutoSync,
  msSyncInProgress,
  msWebhookEnabled,
  msWebhookSubscription,
  msSyncSettings,
  timeEntries,
  bulkPushInProgress,
  // Supabase
  useSupabaseDB,
  supabaseTimeEntries,
  // State setters
  setModal,
  setMsAutoSync,
  setTimeEntries,
  // Functions
  initiateMsCalendarAuth,
  disconnectMsCalendar,
  syncMsCalendar,
  initializeMicrosoftCalendars,
  seedMsCategoryColors,
  recolorMsCalendars,
  backfillMsCategoryColors,
  bulkPushEventsToMicrosoft,
  createWebhookSubscription,
  deleteWebhookSubscription,
  fmtTime,
}) {
  // ========================================================================
  // ROLE DETECTION — Same logic as TimelinePage
  // ========================================================================
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'admin-pa';

  const recentEventsTableProps = getTablePropsFromCode(RECENT_EVENTS_CODE);

  // ========================================================================
  // NON-ADMIN GUARD — Block access entirely
  // Navigation and routing in App.js prevent non-admin from reaching here,
  // but this is a defensive fallback.
  // ========================================================================
  if (!isAdmin) {
    return (
      <div className="brio-flex brio-flex-col brio-analytics-font" style={{ background: '#fff' }}>
        <div className="brio-pageband brio-pageband--header">
          <BrioFrame>
            <div className="brio-page-header__content">
              <div className="brio-page-header__left">

                <p className="brio-analytics-muted brio-mt-xs">Admin access required</p>
              </div>
            </div>
          </BrioFrame>
        </div>
        <BrioFrame>
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <XCircle className="w-12 h-12" style={{ color: 'var(--brio-red)', margin: '0 auto 16px' }} />
            <p className="brio-analytics-muted">
              Microsoft Calendar management is restricted to admin users.
            </p>
          </div>
        </BrioFrame>
      </div>
    );
  }

  // ========================================================================
  // ADMIN VIEW — Full management UI
  // ========================================================================
  return (
    <div className="brio-flex brio-flex-col brio-analytics-font" style={{ background: '#fff' }}>
      {/* PAGE HEADER BAND: Full-width white background, centered content */}
      <div className="brio-pageband brio-pageband--header">
        <BrioFrame>
          <div className="brio-page-header__content">
            <div className="brio-page-header__left">

              <p className="brio-analytics-muted brio-mt-xs">
                {msCalendarConnected
                  ? 'Manage the master BRIO Projects Calendar — synced to all team members'
                  : 'Connect the master calendar to sync project events to Outlook'}
              </p>
            </div>
            <div className="brio-page-header__right">
              {msCalendarConnected && (
                <div className="brio-flex brio-gap-xs">
                  <button 
                    onClick={() => syncMsCalendar()}
                    disabled={msSyncInProgress}
                    className="brio-btn brio-btn-primary brio-flex brio-items-center brio-gap-xs"
                    style={msSyncInProgress ? { opacity: 0.5 } : {}}
                  >
                    <RefreshCw className={`w-4 h-4 ${msSyncInProgress ? 'animate-spin' : ''}`} />
                    {msSyncInProgress ? 'Syncing...' : 'Sync Now'}
                  </button>
                  <button 
                    onClick={() => setModal('msCalendarSettings')}
                    className="brio-btn brio-btn-secondary"
                  >
                    Settings
                  </button>
                  <button
                    onClick={initializeMicrosoftCalendars}
                    className="brio-btn brio-btn-secondary brio-flex brio-items-center brio-gap-xs"
                    title="Create BRIO calendars in Outlook for each event type"
                  >
                    <Calendar className="w-4 h-4" />
                    Initialize Calendars
                  </button>
                  {seedMsCategoryColors && (
                    <button
                      onClick={seedMsCategoryColors}
                      className="brio-btn brio-btn-secondary brio-flex brio-items-center brio-gap-xs"
                      title="Seed BRIO category colors on the Outlook mailbox so pushed events render in BRIO colors"
                    >
                      <Palette className="w-4 h-4" />
                      Seed Outlook Colors
                    </button>
                  )}
                  {recolorMsCalendars && (
                    <button
                      onClick={recolorMsCalendars}
                      className="brio-btn brio-btn-secondary brio-flex brio-items-center brio-gap-xs"
                      title="Recolor each BRIO calendar in Outlook (sidebar dots + default event color)"
                    >
                      <Palette className="w-4 h-4" />
                      Recolor Calendars
                    </button>
                  )}
                  {backfillMsCategoryColors && (
                    <button
                      onClick={backfillMsCategoryColors}
                      className="brio-btn brio-btn-secondary brio-flex brio-items-center brio-gap-xs"
                      title="Patch BRIO categories onto every previously-synced Outlook event"
                    >
                      <Palette className="w-4 h-4" />
                      Backfill Categories
                    </button>
                  )}
                  <button
                    onClick={bulkPushEventsToMicrosoft}
                    disabled={bulkPushInProgress}
                    className={`brio-btn brio-flex brio-items-center brio-gap-xs ${bulkPushInProgress ? 'brio-btn-secondary' : 'brio-btn-success'}`}
                    style={bulkPushInProgress ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                    title="Push unsynced local events to Microsoft Calendar"
                  >
                    <Upload className="w-4 h-4" />
                    {bulkPushInProgress ? 'Pushing...' : 'Push Unsynced'}
                  </button>
                  <button 
                    onClick={disconnectMsCalendar}
                    className="brio-btn brio-btn-danger brio-flex brio-items-center brio-gap-xs"
                  >
                    <Unlink className="w-4 h-4" />
                    Disconnect
                  </button>
                </div>
              )}
            </div>
          </div>
        </BrioFrame>
      </div>

      {/* CONTENT AREA */}
      <BrioFrame>
        <div className="brio-flex brio-flex-col brio-gap-s" style={{ paddingTop: 'var(--brio-space-s)' }}>
          {!msCalendarConnected ? (
            <BrioAnalyticsSurface padding="m">
              <div className="brio-text-center" style={{ padding: '64px 0' }}>
                <Calendar className="w-16 h-16 mx-auto brio-mb-s" style={{ color: 'var(--brio-grey)' }} />
                <h3 className="brio-analytics-section-title brio-text-2xl brio-mb-xs">
                  Connect Master Projects Calendar (Admin)
                </h3>
                <p className="brio-analytics-muted brio-mb-s" style={{ maxWidth: '480px', margin: '0 auto 24px' }}>
                  Connect a Microsoft 365 calendar that will serve as the shared BRIO Projects Calendar.
                  Once connected, project events are synced automatically and all team members can subscribe to it in Outlook.
                </p>
                <button 
                  onClick={initiateMsCalendarAuth}
                  className="brio-btn brio-btn-primary brio-flex brio-items-center brio-gap-xs"
                  style={{ margin: '0 auto', fontSize: '16px', padding: '12px 24px' }}
                >
                  <Link className="w-5 h-5" />
                  Connect Microsoft Calendar
                </button>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', maxWidth: '600px', margin: '32px auto 0' }}>
                  <div style={{ padding: '16px', background: 'var(--brio-grey-light)' }}>
                    <RefreshCw className="w-8 h-8 mx-auto brio-mb-xs" style={{ color: 'var(--brio-gold)' }} />
                    <h4 className="brio-font-medium brio-mb-xs">Auto-Sync</h4>
                    <p className="brio-text-s brio-text-grey">Automatically sync events every 15 minutes</p>
                  </div>
                  <div style={{ padding: '16px', background: 'var(--brio-grey-light)' }}>
                    <Calendar className="w-8 h-8 mx-auto brio-mb-xs" style={{ color: 'var(--brio-gold)' }} />
                    <h4 className="brio-font-medium brio-mb-xs">Shared Calendar</h4>
                    <p className="brio-text-s brio-text-grey">Team subscribes in Outlook — no per-user setup</p>
                  </div>
                  <div style={{ padding: '16px', background: 'var(--brio-grey-light)' }}>
                    <BarChart3 className="w-8 h-8 mx-auto brio-mb-xs" style={{ color: 'var(--brio-gold)' }} />
                    <h4 className="brio-font-medium brio-mb-xs">Analytics</h4>
                    <p className="brio-text-s brio-text-grey">Track time spent in meetings vs work</p>
                  </div>
                </div>
              </div>
            </BrioAnalyticsSurface>
          ) : (
            <>
              {/* Calendar Stats - using code 4c pattern (card grid) */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
                <BrioAnalyticsSurface padding="s">
                  <div style={{ background: 'var(--brio-white)', padding: '16px' }}>
                    <div className="brio-text-s" style={{ color: 'var(--brio-gold)' }}>Total Events</div>
                    <div className="brio-text-2xl brio-font-bold" style={{ color: 'var(--brio-gold-hover)' }}>{msCalendarEvents.length}</div>
                  </div>
                </BrioAnalyticsSurface>
                <BrioAnalyticsSurface padding="s">
                  <div style={{ background: 'var(--brio-white)', padding: '16px' }}>
                    <div className="brio-text-s" style={{ color: 'var(--brio-green)' }}>Synced Entries</div>
                    <div style={{ fontSize: '24px', fontWeight: 700, color: 'var(--brio-green)' }}>
                      {timeEntries.filter(e => e.msCalendarSynced).length}
                    </div>
                  </div>
                </BrioAnalyticsSurface>
                <BrioAnalyticsSurface padding="s">
                  <div style={{ background: 'var(--brio-white)', padding: '16px' }}>
                    <div className="brio-text-s" style={{ color: 'var(--brio-gold)' }}>Sync Mode</div>
                    <div className="brio-text-xl brio-font-bold" style={{ color: 'var(--brio-gold-hover)' }}>
                      {msWebhookEnabled ? '⚡ Smart' : msAutoSync ? '🔄 Auto' : '⏸️ Manual'}
                    </div>
                  </div>
                </BrioAnalyticsSurface>
                <BrioAnalyticsSurface padding="s">
                  <div style={{ background: 'var(--brio-white)', padding: '16px' }}>
                    <div className="brio-text-s" style={{ color: 'var(--brio-orange)' }}>Last Sync</div>
                    <div className="brio-text-s brio-font-bold" style={{ color: 'var(--brio-orange)' }}>
                      {msLastSync ? new Date(msLastSync).toLocaleString() : 'Never'}
                    </div>
                  </div>
                </BrioAnalyticsSurface>
              </div>

              {/* Auto-Sync Toggle — Admin only */}
              <BrioAnalyticsSurface padding="s">
                <div className="brio-flex brio-items-center brio-justify-between" style={{ background: 'var(--brio-gold-light)', padding: '16px', border: '1px solid var(--brio-gold)' }}>
                  <div>
                    <h4 className="brio-font-medium" style={{ color: 'var(--brio-gold-hover)' }}>Automatic Sync (Polling)</h4>
                    <p className="brio-text-s" style={{ color: 'var(--brio-gold)' }}>Sync calendar events every 15 minutes</p>
                  </div>
                  <label className="brio-flex brio-items-center brio-gap-xs" style={{ cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={msAutoSync}
                      onChange={(e) => setMsAutoSync(e.target.checked)}
                      className="brio-modal-input"
                      style={{ width: '20px', height: '20px' }}
                    />
                    <span className="brio-text-s brio-font-medium" style={{ color: 'var(--brio-gold-hover)' }}>
                      {msAutoSync ? 'Enabled' : 'Disabled'}
                    </span>
                  </label>
                </div>
              </BrioAnalyticsSurface>

              {/* Event-Based Sync Toggle (Webhooks) — Admin only */}
              <BrioAnalyticsSurface padding="s">
                <div style={{ background: 'var(--brio-green-light)', padding: '16px', border: '1px solid var(--brio-green)' }}>
                  <div className="brio-flex brio-items-center brio-justify-between">
                    <div>
                      <h4 className="brio-font-medium brio-flex brio-items-center brio-gap-xs" style={{ color: 'var(--brio-green)' }}>
                        ⚡ Event-Based Sync (Webhooks)
                        <span className="brio-badge brio-badge--approved" style={{ fontSize: '10px' }}>
                          Recommended
                        </span>
                      </h4>
                      <p className="brio-text-s brio-mt-xs" style={{ color: 'var(--brio-green)' }}>
                        Instant sync when calendar changes occur (requires webhook URL configuration)
                      </p>
                    </div>
                    <label className="brio-flex brio-items-center brio-gap-xs" style={{ cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={msWebhookEnabled}
                        onChange={async (e) => {
                          if (e.target.checked) {
                            await createWebhookSubscription();
                          } else {
                            if (window.confirm('Disable event-based sync? You will fall back to polling.')) {
                              await deleteWebhookSubscription();
                            }
                          }
                        }}
                        className="brio-modal-input"
                        style={{ width: '20px', height: '20px' }}
                      />
                      <span className="brio-text-s brio-font-medium" style={{ color: 'var(--brio-green)' }}>
                        {msWebhookEnabled ? 'Active' : 'Inactive'}
                      </span>
                    </label>
                  </div>
                  
                  {msWebhookEnabled && msWebhookSubscription && (
                    <div className="brio-mt-s brio-text-xs" style={{ paddingTop: '12px', borderTop: '1px solid var(--brio-green)', color: 'var(--brio-green)' }}>
                      <div className="brio-flex brio-items-center brio-justify-between">
                        <span>📡 Webhook ID: {msWebhookSubscription.id?.substring(0, 8)}...</span>
                        <span>⏰ Expires: {new Date(msWebhookSubscription.expirationDateTime).toLocaleString()}</span>
                      </div>
                    </div>
                  )}
                  
                  {!msWebhookEnabled && (
                    <div className="brio-mt-s" style={{ paddingTop: '12px', borderTop: '1px solid var(--brio-green)' }}>
                      <details className="brio-text-xs" style={{ color: 'var(--brio-green)' }}>
                        <summary style={{ cursor: 'pointer', fontWeight: 500 }}>
                          ℹ️ What's the difference?
                        </summary>
                        <div className="brio-mt-xs" style={{ paddingLeft: '16px' }}>
                          <div>• <strong>Polling (Auto-Sync):</strong> Checks every 15 min, may miss instant changes</div>
                          <div>• <strong>Event-Based (Smart):</strong> Instant notification when calendar changes</div>
                          <div>• <strong>Setup required:</strong> Publicly accessible webhook URL (see docs)</div>
                        </div>
                      </details>
                    </div>
                  )}
                </div>
              </BrioAnalyticsSurface>

              {/* Calendar Events List - using code 1b — Admin only */}
              <BrioAnalyticsSurface padding="m">
                <h3 className="brio-analytics-section-title brio-mb-s">Recent Calendar Events</h3>
                {msCalendarEvents.length === 0 ? (
                  <div className="brio-text-center brio-analytics-muted" style={{ padding: '32px 0' }}>
                    <Calendar className="w-12 h-12 mx-auto brio-mb-s" style={{ color: 'var(--brio-grey)' }} />
                    <p>No calendar events found. Click "Sync Now" to fetch events.</p>
                  </div>
                ) : (
                  <BrioByCode code={RECENT_EVENTS_CODE}>
                    <BrioTable {...recentEventsTableProps}>
                      <thead>
                        <tr>
                          <th>event</th>
                          <th>date & time</th>
                          <th>duration</th>
                          <th className="text-center">status</th>
                          <th className="text-center col-actions">actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {msCalendarEvents.slice(0, 20).map(event => {
                          const linkedEntry = timeEntries.find(e => e.msCalendarEventId === event.id);
                          const startDate = new Date(event.start);
                          const endDate = new Date(event.end);
                          
                          // Validate dates to prevent NaN display
                          const isValidDate = !isNaN(startDate.getTime()) && !isNaN(endDate.getTime());
                          const duration = isValidDate ? (endDate - startDate) / 1000 : 0;
                          
                          // Skip events with invalid dates
                          if (!isValidDate) {
                            console.warn('MS Calendar: Skipping event with invalid date:', event.id);
                            return null;
                          }
                          
                          return (
                            <tr key={event.id} className="brio-row--hoverable">
                              <td>
                                <div className="brio-font-medium">{event.subject}</div>
                                {event.location && <div className="brio-text-xs brio-text-grey">📍 {event.location}</div>}
                                {event.organizer && <div className="brio-text-xs brio-text-grey">👤 {event.organizer}</div>}
                                {event.categories.length > 0 && (
                                  <div className="brio-flex brio-gap-xs brio-mt-xs">
                                    {event.categories.map((cat, idx) => (
                                      <span key={idx} className="brio-badge brio-badge--pending" style={{ fontSize: '9px' }}>
                                        {cat}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </td>
                              <td>
                                <div className="brio-text-grey">
                                  📅 {startDate.toLocaleDateString()}
                                </div>
                                <div className="brio-text-xs brio-text-grey">
                                  🕐 {startDate.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})} - {endDate.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                                </div>
                              </td>
                              <td>
                                <span className="brio-font-medium">⏱️ {fmtTime(duration)}</span>
                              </td>
                              <td className="text-center">
                                {linkedEntry ? (
                                  <span className="brio-badge brio-badge--approved">
                                    Linked to Time Entry
                                  </span>
                                ) : (
                                  <span className="brio-badge brio-badge--pending">
                                    Not Linked
                                  </span>
                                )}
                              </td>
                              <td className="text-center">
                                {!linkedEntry && msSyncSettings.defaultProjectId && msSyncSettings.defaultTaskId && (
                                  <button
                                    onClick={async () => {
                                      const entry = {
                                        id: Date.now(),
                                        userId: currentUser.id,
                                        projectId: msSyncSettings.defaultProjectId,
                                        taskId: msSyncSettings.defaultTaskId,
                                        description: event.subject,
                                        duration: duration,
                                        date: startDate.toISOString().split('T')[0],
                                        billable: true,
                                        notes: `Imported from Microsoft Calendar\nLocation: ${event.location}\nOrganizer: ${event.organizer}`,
                                        approved: false,
                                        msCalendarEventId: event.id,
                                        msCalendarSynced: true
                                      };
                                      if (useSupabaseDB) {
                                        const result = await supabaseTimeEntries.createTimeEntry(entry);
                                        if (!result.success) {
                                          alert('Error creating entry: ' + result.error);
                                          return;
                                        }
                                      } else {
                                        setTimeEntries([...timeEntries, entry]);
                                      }
                                      alert('Time entry created!');
                                    }}
                                    className="brio-btn brio-btn-primary"
                                    style={{ padding: '4px 8px', fontSize: '11px' }}
                                  >
                                    Create Entry
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </BrioTable>
                  </BrioByCode>
                )}
              </BrioAnalyticsSurface>
            </>
          )}
        </div>
      </BrioFrame>
    </div>
  );
}
